import VStack from '@/components/core/VStack/VStack'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@blockpot-dev/block-pot-design-system'
import { XIcon } from 'lucide-react'
import HStack from '@/components/core/HStack/HStack'
import { formatFixedMaxDecimalsGreedy } from '@/utilities/formatters'
import useBlockPotTokenData, { BlockPotTokenState } from '@/hooks/contracts/block-pot-token/useBlockPotTokenData'
import LabeledBalance from '../../common/LabeledBalance/LabeledBalance'
import { ReactNode } from 'react'
import HighlightDivider from '../../common/HighlightDivider/HighlightDivider'

type BPTContent = {
    title: string
    content: ReactNode
}

const CONTENT: BPTContent[] = [
    {
        title: 'What is BPT?',
        content: <>
            Blockpot Token (BPT) is the Blockpot native protocol token and is your key to fully participate in the protocol.
            <br />
            <br />
            You will earn <span className='font-bold text-foreground'>1 BPT</span> for each individual ticket in the lottery!
        </>
    },
    {
        title: 'Redeem',
        content: <>
            You can redeem your BPT to get a discount on your lottery entries. A single BPT can get you a 10% discount on a single entry.
        </>
    },
    {
        title: 'Earn',
        content: <>
            Earn a share of the total pot contributions by staking your BPT. You will be rewarded with dividends proportional to <span className='font-bold text-foreground'>your total</span> BPT staked divided by the <span className='font-bold text-foreground'>global</span> BPT staked.
        </>
    },
    {
        title: 'Vote',
        content: <>
            When you stake BPT, you are eligible to vote on proposals where you have 1 vote per 1 BPT staked.
            <br/>
            <br/>
            When you have <span className='font-bold text-foreground'>100 BPT</span> staked, you are eligible to create a proposal.
        </>
    }
]

export type _BPTDialogProps = {
    open: boolean
    onClose: () => void
    bptState: BlockPotTokenState
}

export function _BPTDialog(props: _BPTDialogProps) {
    const { open, onClose, bptState } = props

    return <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='lg:min-w-[576px]' showCloseButton={false} containerContentClassName='p-6'>
            <DialogHeader className='flex flex-row justify-between'>
                <HStack className='gap-4 items-center justify-between w-full'>
                    <DialogTitle className='uppercase heading-xl font-normal h-auto'>Blockpot Token</DialogTitle>
                    <Button variant='ghost' size='icon' className='size-6 p-0' onClick={onClose}>
                        <XIcon className='size-6' />
                    </Button>
                </HStack>
            </DialogHeader>
            <VStack className='pt-6 gap-6'>
                <HStack className='gap-4 items-center w-full'>
                    <LabeledBalance
                        label='Wallet Balance:'
                        balance={<div className='font-bold'>{formatFixedMaxDecimalsGreedy(bptState.balance, 18, 2)}</div>}
                        imageSrc='/assets/svgs/tokens/bpt-outlined.svg'
                        imageAlt='BPT'
                        className='flex-1'
                    />
                    <LabeledBalance
                        label='Staked Balance:'
                        balance={<div className='font-bold'>{formatFixedMaxDecimalsGreedy(bptState.rewardTrackerAllowance, 18, 2)}</div>}
                        imageSrc='/assets/svgs/tokens/bpt-outlined.svg'
                        imageAlt='BPT'
                        className='flex-1'
                    />
                </HStack>
                <VStack className='gap-0'>
                    {CONTENT.map((content, index) => (
                        <div key={index}>
                            <h3 className='font-bold mb-2'>{content.title}</h3>
                            <p className='text-sm text-foreground opacity-80'>
                                {content.content}
                            </p>
                            {index !== CONTENT.length - 1 && <HighlightDivider className='w-full my-6' direction='horizontal' />}
                        </div>
                    ))}
                </VStack>
            </VStack>
        </DialogContent>
    </Dialog>
}

export type BPTDialogProps = {
    open: boolean
    onClose: () => void
}

export default function BPTDialog(props: BPTDialogProps) {
    const { open, onClose } = props
    const bptState = useBlockPotTokenData()

    return <_BPTDialog
        open={open}
        onClose={onClose}
        bptState={bptState}
    />
}
